import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  Box,
} from "@mui/material";
import localize from "../../../common/localize";
import {
  getArrayWithFieldTypes,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import Combobox from "../../../components/common/Combobox";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import CreatedByText from "../../../components/common/CreatedByText";

export default function AddOrEditFormField({
  model,
  setModel,
  trueFalseTexts,
  setTrueFalseTexts,
}) {
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;

  useEffect(() => {
    if (model.Description) {
      setDescriptionCount(model.Description.length);
    }
  }, [model?.Description]);

  const handleChange = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckedChanged = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.checked,
    });
  };

  const handleFieldTypeChange = (value) => {
    if (value) {
      setModel({
        ...model,
        ValueTypeData: value,
        ValueType: value.Id,
      });

      switch (value.Id) {
        case "BOOLEAN":
          setTrueFalseTexts({
            trueText: localize.yes,
            falseText: localize.no,
          });
          break;

        case "COMBOBOX":
          setTrueFalseTexts(undefined);
          model.Options = "";
          break;

        default:
          setTrueFalseTexts(undefined);
          break;
      }
    }
  };

  const handleChangeOptions = (e) => {
    setTrueFalseTexts({
      ...trueFalseTexts,
      [e.target.name]: e.target.value,
    });
  };

  const handleOptionsChange = (e) => {
    if (e) {
      setModel({
        ...model,
        Options: e.target.value,
      });
      return;
    }

    setModel({
      ...model,
      Options: "",
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <form className="w-full flex flex-col gap-2">
        <div className="flex flex-wrap my-2 w-full">
          <Combobox
            isRequired
            id="ValueType"
            handleValueChange={handleFieldTypeChange}
            fieldValue={model?.ValueTypeData}
            fieldOptions={getArrayWithFieldTypes()}
            fieldLabel={localize.field_type}
          />
        </div>

        <div className="flex flex-wrap my-2 w-full">
          <FormControl required fullWidth>
            <TextField
              required
              InputLabelProps={getInputLabelProps()}
              inputProps={getInputProps()}
              size="small"
              fullWidth
              id="outlined-basic"
              name="Title"
              label={localize.title}
              variant="outlined"
              onChange={handleChange}
              value={model.Title}
            />
          </FormControl>
        </div>

        {model?.ValueType === "COMBOBOX" && (
          <div className="w-full">
            <Box className="flex flex-col gap-3">
              <Alert severity="info">
                <label className="text-xs">
                  {localize.combobox_helper_text}
                </label>
              </Alert>
              <FormControl required fullWidth>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    style: {
                      fontSize: "0.8em",
                    },
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  id="outlined-basic"
                  name="Options"
                  label={localize.options}
                  variant="outlined"
                  onChange={handleOptionsChange}
                  value={model?.Options}
                />
              </FormControl>
            </Box>
          </div>
        )}

        {trueFalseTexts && (
          <div className="flex justify-between my-2 w-full">
            <div className="mr-2 w-full">
              <FormControl required fullWidth>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  name="trueText"
                  label={localize.true_value}
                  variant="outlined"
                  onChange={handleChangeOptions}
                  value={trueFalseTexts?.trueText}
                />
              </FormControl>
            </div>

            <FormControl required fullWidth>
              <TextField
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                size="small"
                fullWidth
                id="outlined-basic"
                name="falseText"
                label={localize.false_value}
                variant="outlined"
                onChange={handleChangeOptions}
                value={trueFalseTexts?.falseText}
              />
            </FormControl>
          </div>
        )}

        <div className="flex flex-wrap my-2 w-full">
          <FormControl required fullWidth>
            <TextField
              InputLabelProps={getInputLabelProps()}
              inputProps={{
                style: {
                  fontSize: "0.8em",
                },
                maxLength: maximumCharactersForDescription,
              }}
              size="small"
              fullWidth
              multiline
              rows={3}
              id="outlined-basic"
              name="Description"
              label={localize.help_text}
              variant="outlined"
              onChange={handleChange}
              value={model.Description}
              helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
            />
          </FormControl>
        </div>

        <div className="flex flex-col gap-2">
          <FormControlLabel
            size="small"
            name="VisibleOnMobile"
            control={
              <Checkbox
                size="small"
                name="VisibleOnMobile"
                checked={model.VisibleOnMobile}
                onChange={handleCheckedChanged}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  useFieldTitleStyle
                  useEventTitleStyle
                  title={localize.visible_on_mobile}
                  subtitle={localize.visible_on_mobile_info}
                />
              </span>
            }
          />

          <FormControlLabel
            size="small"
            name="Mandatory"
            control={
              <Checkbox
                size="small"
                name="Mandatory"
                checked={model.Mandatory}
                onChange={handleCheckedChanged}
              />
            }
            label={
              <span style={{ fontSize: "0.8em" }}>
                <PageTitleSubtitle
                  useFieldTitleStyle
                  useEventTitleStyle
                  title={localize.mandatory}
                  subtitle={localize.mandatory_info}
                />
              </span>
            }
          />

          {model?.ValueType === "STRING" && (
            <FormControlLabel
              size="small"
              name="Multiline"
              control={
                <Checkbox
                  size="small"
                  name="Multiline"
                  checked={model.Multiline}
                  onChange={handleCheckedChanged}
                />
              }
              label={
                <span style={{ fontSize: "0.8em" }}>
                  {localize.multiline_text}
                </span>
              }
            />
          )}
        </div>
      </form>

      <CreatedByText entity={model} />
    </div>
  );
}
